import { useEffect, useState } from "react";
import {
  Easing,
  runOnJS,
  type SharedValue,
  useSharedValue,
  withTiming,
} from "@-/com.npmjs.react-native-reanimated";

const ENTER_DURATION_MS = 220;
const EXIT_DURATION_MS = 180;

/**
 * Drives the selection bar's slide-in / slide-out as a *manual transform* rather than a
 * reanimated `entering`/`exiting` layout animation.
 *
 * This is deliberate, and it is the fix for the intermittent white gap that used to appear
 * between the bar's top edge and the tab bar. With `entering={SlideInDown}`, a Fabric commit
 * landing during the 220ms entering window cancels reanimated's final snap-to-layout: the view
 * keeps its last in-flight frame (measured residuals of +3.34pt and +4.18pt) and reanimated
 * holds that stale layout override for the view tag *indefinitely* — later commits into the
 * subtree never heal it. Because this bar mounts exactly when app state churns (selection
 * seeding, context re-renders), a mid-animation commit was near-guaranteed, hence "sometimes".
 *
 * A manual transform has no such failure mode. The view's *layout* is permanently pinned at
 * `bottom: 0`; only `translateY` moves, and `withTiming` always converges on its target. There
 * is no layout-override state for a commit race to freeze, so the resting position is exactly
 * flush with the tab bar by construction.
 *
 * Returns `hiddenFraction` (1 = fully off-screen, 0 = resting; the bar turns it into a
 * `translateY` against its own height) plus `isMounted`, which stays true through the exit
 * animation so the bar keeps rendering until it is fully off-screen. The overlay's gate is built
 * from these, so the parent owns the slide's lifetime and the bar mounts only while it's visible.
 */
export function useMorphemeSelectionOverlaySlide({ isSelecting }: { isSelecting: boolean }): {
  hiddenFraction: SharedValue<number>;
  isMounted: boolean;
} {
  // Kept true for the duration of the slide-out so the bar's contents survive until it is
  // off-screen; flipped false by the exit timing callback.
  const [isMounted, setIsMounted] = useState(isSelecting);

  // Fraction of the bar that is hidden below the bottom edge: 1 = fully off-screen, 0 = resting
  // flush against the tab-bar footprint. Stored as a fraction (not points) so a height change
  // mid-animation — e.g. a safe-area inset arriving late — cannot strand the bar at a stale
  // pixel offset.
  const hiddenFraction = useSharedValue(isSelecting ? 0 : 1);

  useEffect(() => {
    if (isSelecting) {
      setIsMounted(true);
      hiddenFraction.set(
        withTiming(0, {
          duration: ENTER_DURATION_MS,
          easing: Easing.out(Easing.cubic),
        }),
      );
      return;
    }

    hiddenFraction.set(
      withTiming(1, { duration: EXIT_DURATION_MS, easing: Easing.in(Easing.cubic) }, (finished) => {
        if (finished === true) {
          runOnJS(setIsMounted)(false);
        }
      }),
    );
  }, [hiddenFraction, isSelecting]);

  return { hiddenFraction, isMounted };
}
